import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminAnalyticsService } from '../../services/analyticsService';

export const fetchAnalytics = createAsyncThunk(
  'analytics/fetchAnalytics',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await adminAnalyticsService.getAnalytics(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchAnalyticsSummary = createAsyncThunk(
  'analytics/fetchSummary',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await adminAnalyticsService.getSummary(days);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const exportAnalytics = createAsyncThunk(
  'analytics/export',
  async ({ filters, format }, { rejectWithValue }) => {
    try {
      await adminAnalyticsService.exportAnalytics(filters, format);
      return { success: true, message: `Analytics exported as ${format.toUpperCase()}` };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  analytics: {
    stats: {},
    transactions: [],
    pagination: {}
  },
  summary: {
    totalRevenue: 0,
    transactionsByType: [],
    byPaymentMethod: [],
    userStats: {},
    popularServices: [],
    dailyRevenue: [],
    period: ''
  },
  isLoading: false,
  error: null,
  filters: {
    startDate: null,
    endDate: null,
    paymentMethod: '',
    transactionType: '',
    transactionStatus: '',
    page: 1,
    limit: 10
  }
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Analytics
    builder
      .addCase(fetchAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch analytics';
      });

    // Fetch Summary
    builder
      .addCase(fetchAnalyticsSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.summary = action.payload.summary;
      })
      .addCase(fetchAnalyticsSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch summary';
      });

    // Export Analytics
    builder
      .addCase(exportAnalytics.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(exportAnalytics.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to export analytics';
      });
  }
});

export const { setFilters, resetFilters, clearError } = analyticsSlice.actions;
export default analyticsSlice.reducer;
